/**
 * ADMIN MODULE — Support Tickets inbox
 * Brand Command staff view of every support ticket across tenants. Admin-only;
 * rendered by SupportTicketsView under the RequireSuperAdmin gate.
 *
 * The ticket thread, attachments, and service layer stay in /shared because the
 * customer-facing Help page ("submit a ticket") uses them too. Importing them
 * here is admin -> shared, which the module boundary allows.
 */

import { useEffect, useState } from "react";
import { adminListTickets, setTicketStatus, deleteTicket, TICKET_STATUSES } from "../../../shared/utils/ticketService.js";
import { usePlatformAdmin } from "../../../shared/hooks/usePlatformAdmin.js";
import TicketThread from "../../../shared/components/TicketThread.jsx";

const STATUS_META = {
  open:     { label: "Open",     color: "#2563eb" },
  working:  { label: "Working",  color: "#d97706" },
  deferred: { label: "Deferred", color: "#7c3aed" },
  resolved: { label: "Resolved", color: "#16a34a" },
  closed:   { label: "Closed",   color: "#6b7280" },
};

function fmtDate(d) {
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// Brand Command staff "User Tickets" — grid view of every ticket across tenants.
export default function AdminTickets() {
  const { isAdmin, checked } = usePlatformAdmin();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busyId, setBusyId] = useState(null);

  async function load() {
    setErr(null);
    try {
      setTickets(await adminListTickets());
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isAdmin) load();
    else if (checked) setLoading(false);
  }, [isAdmin, checked]);

  async function changeStatus(t, status) {
    setBusyId(t.id);
    try {
      await setTicketStatus(t.id, status);
      setTickets((prev) => prev.map((x) => (x.id === t.id ? { ...x, status } : x)));
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusyId(null);
    }
  }

  async function remove(t) {
    if (!window.confirm(`Delete BC-${t.ticket_number} from ${t.submitter_email || "this user"}? This can't be undone.`)) return;
    setBusyId(t.id);
    try {
      await deleteTicket(t.id);
      setTickets((prev) => prev.filter((x) => x.id !== t.id));
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusyId(null);
    }
  }

  if (!checked || loading) {
    return <div style={{ maxWidth: 820, margin: "0 auto" }}><section className="settings-card"><p className="muted">Loading…</p></section></div>;
  }
  if (!isAdmin) {
    return <div style={{ maxWidth: 820, margin: "0 auto" }}><section className="settings-card"><h2>User Tickets</h2><p className="error">You don't have access to this page.</p></section></div>;
  }

  // Detail view: the conversation thread.
  if (selected) {
    return (
      <div style={{ maxWidth: 980, margin: "0 auto", width: "100%" }}>
        <section className="settings-card wide">
          <TicketThread
            ticketId={selected.id}
            isAdmin
            submitterEmail={selected.submitter_email}
            onBack={() => { setSelected(null); load(); }}
            onChanged={load}
          />
        </section>
      </div>
    );
  }

  const counts = { all: tickets.length };
  for (const s of TICKET_STATUSES) counts[s] = tickets.filter((t) => t.status === s).length;
  const shown = filter === "all" ? tickets : tickets.filter((t) => t.status === filter);
  const cards = [{ key: "all", label: "All", color: "var(--muted)" }, ...TICKET_STATUSES.map((s) => ({ key: s, ...STATUS_META[s] }))];

  return (
    <div style={{ width: "100%" }}>
      <section className="settings-card wide">
        {/* Summary count cards (also act as status filters) */}
        <div className="tkt-cards">
          {cards.map((c) => (
            <button
              key={c.key}
              type="button"
              className={`tkt-card${filter === c.key ? " active" : ""}`}
              style={{ "--tkt-accent": c.color }}
              onClick={() => setFilter(c.key)}
            >
              <span className="tkt-card-value">{counts[c.key] ?? 0}</span>
              <span className="tkt-card-label">{c.label}</span>
            </button>
          ))}
        </div>

        {err && <p className="error" style={{ marginTop: 14 }}>{err}</p>}

        {shown.length === 0 ? (
          <p className="muted" style={{ marginTop: 16 }}>No tickets{filter === "all" ? " yet" : ` that are ${filter}`}.</p>
        ) : (
          <div className="tkt-grid-wrap">
            <table className="tkt-grid">
              <thead>
                <tr>
                  <th>Ticket</th>
                  <th>Date</th>
                  <th>Company</th>
                  <th>Submitted by</th>
                  <th>Subject</th>
                  <th>Category</th>
                  <th>Area</th>
                  <th>Files</th>
                  <th>Status</th>
                  <th aria-label="Actions"></th>
                </tr>
              </thead>
              <tbody>
                {shown.map((t) => (
                  <tr key={t.id} onClick={() => setSelected(t)} style={{ cursor: "pointer" }}>
                    <td className="tkt-num">BC-{t.ticket_number}</td>
                    <td className="muted tkt-nowrap">{fmtDate(t.last_reply_at || t.created_at)}</td>
                    <td>{t.account_name || <span className="muted">—</span>}</td>
                    <td className="tkt-email">{t.submitter_email || <span className="muted">—</span>}</td>
                    <td>
                      {t.subject}
                      {t.last_reply_role === "submitter" && <strong style={{ color: "var(--primary)" }}> · awaiting reply</strong>}
                    </td>
                    <td>{t.category || <span className="muted">—</span>}</td>
                    <td>{t.module || <span className="muted">—</span>}</td>
                    <td>{t.attachment_count > 0 ? `📎 ${t.attachment_count}` : <span className="muted">—</span>}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <select
                        value={t.status}
                        disabled={busyId === t.id}
                        onChange={(e) => changeStatus(t, e.target.value)}
                        style={{ width: "auto", margin: 0, fontSize: 12, borderLeft: `3px solid ${STATUS_META[t.status]?.color || "var(--border)"}` }}
                      >
                        {TICKET_STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s]?.label || s}</option>)}
                      </select>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="link"
                        style={{ padding: 0, color: "var(--error)" }}
                        disabled={busyId === t.id}
                        onClick={() => remove(t)}
                        title="Delete ticket"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
